import type { StylingProps } from '@builder4/shared/types/layout';
import type { PaddingMarginValues } from '@/types/layout';
import { useEffect, useState } from 'react';
import { PaddingMarginFields } from './PaddingMarginFields';
import { useAppSelector } from '@/store/hooks';
import { selectScale } from '@/store/zoom/selectors';
import { isImageNode } from '@/utils/helperFunc';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { useInspectorNode } from '@/hooks/useInspectorNode';
import type { ContainerProps } from '@builder4/shared/components/Container';
interface RadiusControlProps {
  onFocus?: () => void;
  onBlur?: () => void;
  value: PaddingMarginValues;
}

export const NodeRadiusSettingsV2: React.FC<RadiusControlProps> = ({ onFocus, onBlur, value }) => {
  const scale = useAppSelector(selectScale);
  const [maxRadius, setMaxRadius] = useState(100);
  const [variant, setVariant] = useState<'locked' | 'unlocked'>('locked');
  const { props: nodeProps, node, id, actions } = useInspectorNode<Partial<ContainerProps>>();
  const displayName = node?.data.displayName || '';
  const { setBPProp, getBPProp } = useBreakpointAwareProp(displayName);
  // Calculate max radius on mount
  useEffect(() => {
    if (node && isImageNode(node) && node.dom) {
      const rect = node.dom.getBoundingClientRect();
      const minDimension = Math.min(rect.width, rect.height) / scale;
      setMaxRadius(Math.floor(minDimension / 2));
    }
  }, [node, scale]);

  useEffect(() => {
    const start = getBPProp(nodeProps, 'borderRadius') || [0, 0, 0, 0];
    setVariant(start.every((v: number) => v == start[0]) ? 'locked' : 'unlocked');
  }, []);

  const onChangeHandler = (newValues: PaddingMarginValues) => {
    actions.history.ignore().setProp(id, (props: Partial<StylingProps>) => {
      setBPProp(props, 'borderRadius', [
        newValues.top,
        newValues.right,
        newValues.bottom,
        newValues.left,
      ]);
    });
  };

  const handleChangeComplete = (
    newValues: PaddingMarginValues,
    initialValues: PaddingMarginValues,
  ) => {
    actions.history.ignore().setProp(id, (props: Partial<StylingProps>) => {
      setBPProp(props, 'borderRadius', [
        initialValues.top,
        initialValues.right,
        initialValues.bottom,
        initialValues.left,
      ]);
    });
    actions.setProp(id, (props: Partial<StylingProps>) => {
      setBPProp(props, 'borderRadius', [
        newValues.top,
        newValues.right,
        newValues.bottom,
        newValues.left,
      ]);
    });
  };

  const handleBlur = () => {
    onBlur?.();
  };

  return (
    <PaddingMarginFields
      label="Corners"
      className="w-full max-w-[216px]"
      value={value}
      variant={variant}
      setVariant={setVariant}
      onChangeComplete={handleChangeComplete}
      onChange={onChangeHandler}
      onFocus={onFocus}
      onBlur={handleBlur}
      min={0}
      max={maxRadius}
    />
  );
};
